import React from 'react';
import SectionHeading from '../components/ui/SectionHeading';
import TestimonialCard from '../components/ui/TestimonialCard';
import { Currency as Transparency, HeartHandshake, MessageSquare } from 'lucide-react';
import Button from '../components/ui/Button';

const CoreValues: React.FC = () => {
  const values = [
    {
      icon: Transparency,
      title: 'Transparency',
      description: 'We believe in maintaining a high level of transparency in our operations, from property inspections to resident concerns. We believe in open dialogue and collaboration to achieve the best, balanced outcomes for your properties.'
    },
    {
      icon: HeartHandshake,
      title: 'Integrity',
      description: 'We act with integrity towards all parties, focusing on a smooth, hands-off approach for property owners while emphasizing happiness of residents as a key core value. We believe in mutually beneficial outcomes in all scenarios.'
    },
    {
      icon: MessageSquare,
      title: 'Communication',
      description: 'Consistent communication is the cornerstone of our property management approach. Rest assured that we will take care of tenants, toilets, and trash while keeping you in the loop. You can choose the frequency of updates and more.'
    }
  ];

  const testimonials = [
    {
      content: 'RentSparks and Julia has managed my rental properties for over 4 years, and their transparency and professionalism is great. Highly recommended!',
      author: 'Rose L.',
      role: 'Condomium and Fiveplex Owner in Los Angeles County',
      rating: 5
    },
    {
      content: 'As a first-time rental property investor, I was anxious about picking the right tenant. RentSparks made the process stress-free with their collaboration, communication, and quick responses to tenant requests. Julia also had helpful suggestions and ideas for a successful renovation.',
      author: 'Michael H.',
      role: 'Real Estate Investor in Santa Monica, CA',
      rating: 5
    },
    {
      content: 'The team really cares about building lasting relationships. They\'ve turned my property management into peace of mind.',
      author: 'Jersey S.',
      role: 'Owner of 3+ Residential & Commercial Units in Southern CA',
      rating: 5
    }
  ];

  return (
    <section id="about" className="py-20 bg-[#FFF8ED] relative">
      <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-[#FAFAF5] to-transparent"></div>
      <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-[#FAFAF5] to-transparent"></div>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <SectionHeading 
          title="Our Core Values" 
          subtitle="We're committed to building enduring partnerships with property owners through these guiding first principles.  Our central goal is long-term client happiness that helps to grow your real estate portfolio and wealth."
        />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
          {values.map((value, index) => {
            const Icon = value.icon;
            return (
              <div 
                key={index} 
                className="bg-light rounded-lg p-8 text-center shadow-custom hover:shadow-custom-hover transition-shadow duration-300"
              >
                <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-primary/20 mb-6">
                  <Icon className="h-8 w-8 text-secondary" strokeWidth={1.5} />
                </div>
                <h3 className="font-abadi text-xl font-medium mb-3">{value.title}</h3>
                <p className="text-dark-gray">{value.description}</p>
              </div>
            );
          })}
        </div>

        <SectionHeading 
          title="What Our Clients Say" 
          subtitle="Hear from property owners who have experienced the RentSparks difference."
        />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {testimonials.map((testimonial, index) => (
            <TestimonialCard 
              key={index}
              content={testimonial.content}
              author={testimonial.author}
              role={testimonial.role}
              rating={testimonial.rating}
            />
          ))}
        </div>

        <div className="text-center">
          <a 
            href="https://maps.app.goo.gl/BWs675P4a5xr58Nz6?g_st=com.google.maps.preview.copy" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-white px-6 py-3 rounded-lg shadow-md hover:shadow-lg transition-all duration-200"
          >
            <svg viewBox="0 0 24 24" className="w-6 h-6">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            <span className="text-gray-700 font-medium">Our Google Reviews</span>
          </a>
        </div>
      </div>
    </section>
  );
};

export default CoreValues;